// ─────────────────────────────────────────────────────────────────────────
// AuthGuard — the ONE central place that protects the whole app.
//
// Problem it solves: when a page (Admin, Jobs, Candidates, …) is opened
// WITHOUT a valid login session, the data APIs throw a 401 ("Authentication
// required") and the page silently renders empty — "no module" — with no
// explanation. That happens both in this app and in any host project that
// consumes @realtek/core-theme and drops a page like <AdminPage /> onto a route.
//
// Instead of editing every page, wrap once and every child is guarded:
//
//   import { AuthGuard } from '@realtek/core-theme';
//   import '@realtek/core-theme/style.css';
//
//   <BrowserRouter>
//     <AuthGuard>
//       <Routes> … your routes / pages … </Routes>
//     </AuthGuard>
//   </BrowserRouter>
//
// When there is no session it shows a clear "you haven't logged in" screen
// with a Login button; when a session exists it renders the children untouched.
// It also re-checks live on login / logout / cross-tab storage changes.
// ─────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { getStoredToken } from '../../services/authApi';
import '../../styles/AuthGuard.css';

const DEFAULT_TITLE = "You're not logged in";
const DEFAULT_MESSAGE =
  "You haven't logged into this session. Please login to access the data.";

function hasSession() {
  try {
    return Boolean(getStoredToken());
  } catch {
    return false;
  }
}

export default function AuthGuard({
  children,
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
  loginPath = '/login',
  loginLabel = 'Go to Login',
  onLogin,
  fallback,
}) {
  const [authed, setAuthed] = useState(hasSession);

  useEffect(() => {
    const recheck = () => setAuthed(hasSession());
    // Our own login/logout events + cross-tab localStorage changes.
    window.addEventListener('auth:login', recheck);
    window.addEventListener('auth:logout', recheck);
    window.addEventListener('storage', recheck);
    // Re-check when the user returns to the tab (token may have expired/cleared).
    window.addEventListener('focus', recheck);
    return () => {
      window.removeEventListener('auth:login', recheck);
      window.removeEventListener('auth:logout', recheck);
      window.removeEventListener('storage', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, []);

  if (authed) return children;

  // Caller-supplied custom screen wins over the default one.
  if (fallback !== undefined) return fallback;

  const goToLogin = () => {
    if (typeof onLogin === 'function') {
      onLogin();
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.assign(loginPath);
    }
  };

  return (
    <div className="ct-auth-guard">
      <div className="ct-auth-guard__card">
        <Result
          status="403"
          icon={<LockOutlined className="ct-auth-guard__icon" />}
          title={title}
          subTitle={message}
          extra={
            <Button type="primary" size="large" onClick={goToLogin}>
              {loginLabel}
            </Button>
          }
        />
      </div>
    </div>
  );
}

AuthGuard.propTypes = {
  children: PropTypes.node,
  title: PropTypes.node,
  message: PropTypes.node,
  loginPath: PropTypes.string,
  loginLabel: PropTypes.node,
  onLogin: PropTypes.func,
  fallback: PropTypes.node,
};
